// Smart Scheduling Assistant for TradeMate Pro
// Uses existing schedule_events table to find optimal time slots

// Supabase configuration
import { getSupabaseClient } from './supabaseClient';
import { fmtDate, fmtTime } from '../utils/dateFormatters';
import { DAY_NAMES, DEFAULT_BUSINESS_HOURS, normalizeBusinessHours, businessHoursEnvelope } from '@tradesmatepro/shared-ui/utils/businessHours';

/**
 * Round time to next 15-minute interval for professional scheduling
 * @param {Date} date - Date to round
 * @returns {Date} - Rounded date
 */
export const roundToNext15Minutes = (date) => {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();
  const remainder = minutes % 15;

  if (remainder !== 0) {
    // Round up to next 15-minute interval
    rounded.setMinutes(minutes + (15 - remainder));
    rounded.setSeconds(0);
    rounded.setMilliseconds(0);
  } else {
    // Already on 15-minute boundary, just clean up seconds/milliseconds
    rounded.setSeconds(0);
    rounded.setMilliseconds(0);
  }

  return rounded;
};

/**
 * Check if a time is on a 15-minute boundary
 * @param {Date} date - Date to check
 * @returns {boolean} - True if on 15-minute boundary
 */
export const isOn15MinuteBoundary = (date) => {
  return date.getMinutes() % 15 === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0;
};

/**
 * Generate clean 15-minute interval time slots
 * @param {Date} startTime - Start of time range
 * @param {Date} endTime - End of time range
 * @param {number} intervalMinutes - Interval between slots (default 15)
 * @returns {Array} - Array of time slots on 15-minute boundaries
 */
export const generateCleanTimeSlots = (startTime, endTime, intervalMinutes = 15) => {
  const slots = [];
  let currentTime = roundToNext15Minutes(new Date(startTime));

  while (currentTime < endTime) {
    slots.push(new Date(currentTime));
    currentTime.setMinutes(currentTime.getMinutes() + intervalMinutes);
  }

  return slots;
};

// book_work_order refusals about the chosen time itself (private.booking_refusal): the openings
// shown are stale, so reload them. Only 'slot_taken' means someone else booked it.
export const SLOT_REFUSAL_CODES = ['slot_taken', 'slot_in_past', 'slot_unavailable', 'tech_unavailable', 'over_capacity', 'invalid_duration'];

/**
 * Default scheduling settings
 */
export const DEFAULT_SCHEDULING_SETTINGS = {
  job_buffer_minutes: 30, // Legacy - for backward compatibility
  default_buffer_before_minutes: 30,
  default_buffer_after_minutes: 30,
  enable_customer_self_scheduling: false,
  auto_approve_customer_selections: false,
  business_hours_start: '07:30',
  business_hours_end: '17:00',
  working_days: [1, 2, 3, 4, 5], // Monday to Friday
  min_advance_booking_hours: 1,
  max_advance_booking_days: 30
};

// ── Canonical business hours (per-day, industry standard) ──────────────────────
// `company_settings.business_hours` is the single source of truth: one entry per
// day, keyed by NAME (no 0-vs-1-vs-7 ambiguity), each with its own open + hours.
// The legacy fields (business_hours_start/end + working_days) are DERIVED from
// this for back-compat until the server slot functions / calendar repoint.
// DAY_NAMES, DEFAULT_BUSINESS_HOURS and normalizeBusinessHours live in the shared package so the
// customer portal reads business hours through the same code (2026-10-29).
export { DAY_NAMES, DEFAULT_BUSINESS_HOURS, normalizeBusinessHours };

// Build per-day hours from the legacy single-hours + working-day-index model
// (used only when a row predates the business_hours backfill).
export const buildBusinessHoursFromLegacy = (workingDayIdxs, start, end) => {
  const open = new Set(workingDayIdxs || []);
  const s = String(start || '08:00').slice(0, 5);
  const e = String(end || '17:00').slice(0, 5);
  const out = {};
  DAY_NAMES.forEach((name, idx) => { out[name] = { open: open.has(idx), start: s, end: e }; });
  return out;
};

// Transitional bridge: derive the legacy-shaped fields FROM canonical business_hours
// so server slot functions / calendar keep working until they repoint. The single
// start/end is the envelope across open days (earliest start, latest end).
export const deriveLegacyFromBusinessHours = (bh) => {
  const { openIdxs, start: minStart, end: maxEnd } = businessHoursEnvelope(bh);
  return {
    working_days: openIdxs.length ? openIdxs : DEFAULT_SCHEDULING_SETTINGS.working_days,
    business_hours_start: minStart || DEFAULT_SCHEDULING_SETTINGS.business_hours_start,
    business_hours_end: maxEnd || DEFAULT_SCHEDULING_SETTINGS.business_hours_end
  };
};

// FullCalendar `businessHours` from canonical per-day business_hours: one entry per open
// day with THAT day's own hours (an envelope across days shades a 9-2 Saturday as 8-5).
export const toFullCalendarBusinessHours = (bh) => {
  const out = [];
  DAY_NAMES.forEach((name, idx) => {
    const d = bh && bh[name];
    if (d && d.open && d.start && d.end) out.push({ daysOfWeek: [idx], startTime: d.start, endTime: d.end });
  });
  return out;
};

/**
 * Get company scheduling settings
 * @param {string} companyId - Company ID
 * @returns {Object} - Scheduling settings
 */
const SCHEDULING_SETTINGS_COLUMNS = ['job_buffer_minutes', 'default_buffer_before_minutes', 'default_buffer_after_minutes', 'enable_customer_self_scheduling', 'auto_approve_customer_selections', 'business_hours_start', 'business_hours_end', 'working_days', 'min_advance_booking_hours', 'max_advance_booking_days'];

export const getSchedulingSettings = async (companyId, { fresh = true } = {}) => {
  try {
    const { supabase } = await import('./supabaseClient');
    // Read through the settings row's owner (SettingsService), FRESH by default: the Smart
    // Scheduler's buffer and working-hours checks always read the current row, and an admin
    // may change them from another device. Fresh also refills the owner's cache.
    // `fresh: false` is for display-only readers (the Dashboard's capacity numbers), which may
    // use the owner's cached row; scheduling paths keep the default.
    const { default: settingsService } = await import('../services/SettingsService');
    const business = await settingsService.getBusinessSettings(companyId, { fresh });
    const rows = business ? Object.fromEntries(SCHEDULING_SETTINGS_COLUMNS.map((k) => [k, business[k]])) : null;

    if (rows) {
      const settings = { ...DEFAULT_SCHEDULING_SETTINGS, ...rows };
      // Ensure buffer settings exist (backward compatibility). Only null/undefined is
      // "unset" — a stored 0 is a real value (#5198: `!0` fell through to the legacy
      // job_buffer_minutes, so a saved 0 read back as 120).
      if (settings.default_buffer_before_minutes == null) {
        settings.default_buffer_before_minutes = settings.job_buffer_minutes ?? 30;
      }
      if (settings.default_buffer_after_minutes == null) {
        settings.default_buffer_after_minutes = settings.job_buffer_minutes ?? 30;
      }
      // Canonical: business_hours (per-day, by day name). If present it's the
      // source of truth; otherwise build it from the legacy fields (pre-backfill
      // rows). Then DERIVE the legacy-shaped fields (working_days, start, end)
      // FROM it so every consumer reads ONE source.
      const dayMap = { sunday:0, monday:1, tuesday:2, wednesday:3, thursday:4, friday:5, saturday:6 };
      const legacyWorkingDays = (() => {
        let wd = settings.working_days;
        if (typeof wd === 'string') { try { wd = JSON.parse(wd); } catch {} }
        if (Array.isArray(wd)) return wd.filter(n => typeof n === 'number');
        if (wd && typeof wd === 'object') {
          return Object.entries(wd).filter(([, v]) => !!v).map(([k]) => dayMap[String(k).toLowerCase()]).filter(v => v !== undefined);
        }
        return DEFAULT_SCHEDULING_SETTINGS.working_days;
      })();
      // business_hours (canonical, per-day) lives on the base company_settings
      // table — the `settings` view doesn't expose it — so read it directly.
      let bhRaw = null;
      try {
        const { data: bhRow } = await supabase
          .from('company_settings')
          .select('business_hours')
          .eq('company_id', companyId)
          .maybeSingle();
        bhRaw = bhRow?.business_hours || null;
      } catch { /* fall back to legacy below */ }
      const bh = (bhRaw && Object.keys(bhRaw).length > 0)
        ? normalizeBusinessHours(bhRaw)
        : buildBusinessHoursFromLegacy(legacyWorkingDays, settings.business_hours_start, settings.business_hours_end);
      settings.business_hours = bh;
      const legacy = deriveLegacyFromBusinessHours(bh);
      settings.working_days = legacy.working_days;
      settings.business_hours_start = legacy.business_hours_start;
      settings.business_hours_end = legacy.business_hours_end;
      // Sanitize settings to avoid pathological gating
      if (!Number.isFinite(settings.min_advance_booking_hours) || settings.min_advance_booking_hours < 0) {
        settings.min_advance_booking_hours = DEFAULT_SCHEDULING_SETTINGS.min_advance_booking_hours;
      }
      if (!Number.isFinite(settings.max_advance_booking_days) || settings.max_advance_booking_days <= 0) {
        settings.max_advance_booking_days = DEFAULT_SCHEDULING_SETTINGS.max_advance_booking_days;
      }
      if (!settings.business_hours_start) settings.business_hours_start = DEFAULT_SCHEDULING_SETTINGS.business_hours_start;
      if (!settings.business_hours_end) settings.business_hours_end = DEFAULT_SCHEDULING_SETTINGS.business_hours_end;

      settings.company_id = companyId;
      return settings;
    }



    return { ...DEFAULT_SCHEDULING_SETTINGS, business_hours: DEFAULT_BUSINESS_HOURS, company_id: companyId };
  } catch (error) {
    console.error('Error fetching scheduling settings:', error);
    return { ...DEFAULT_SCHEDULING_SETTINGS, business_hours: DEFAULT_BUSINESS_HOURS, company_id: companyId };
  }
};

/**
 * Get employee's existing schedule events from both schedule_events and work_orders
 * @param {string} employeeId - Employee ID
 * @param {Date} startDate - Start date for search
 * @param {Date} endDate - End date for search
 * @returns {Array} - Array of schedule events
 */
export const getEmployeeSchedule = async (employeeId, companyId, startDate, endDate) => {
  try {
    const { supabase } = await import('./supabaseClient');

    console.log(`🔍 CONFLICT CHECK: Getting schedule for employee ${employeeId} from ${startDate.toISOString()} to ${endDate.toISOString()}`);

    // Resolve user_id and employee_id based on provided identifier (supports users.id or employees.id)
    let resolvedUserId = null;
    let resolvedEmployeeId = null;

    // Try as user_id first
    const { data: empByUser } = await supabase
      .from('employees')
      .select('id,user_id')
      .eq('user_id', employeeId)
      .maybeSingle();

    if (empByUser) {
      resolvedUserId = empByUser.user_id;
      resolvedEmployeeId = empByUser.id;
    }

    // If not found, try as employee_id
    if (!resolvedEmployeeId) {
      const { data: empById } = await supabase
        .from('employees')
        .select('id,user_id')
        .eq('id', employeeId)
        .maybeSingle();

      if (empById) {
        resolvedEmployeeId = empById.id;
        resolvedUserId = empById.user_id;
      }
    }

    // Fallback: assume provided is a users.id
    if (!resolvedUserId) resolvedUserId = employeeId;

    let allEvents = [];

    // The four reads below are independent of each other, so they run together (page speed
    // 2026-10-25: they were awaited one after another on every conflict check). The MERGE is
    // unchanged and in the same order: user events, then employee events not already present,
    // then work orders, then time off.
    const startISO = startDate.toISOString();
    const endISO = endDate.toISOString();
    const [
      { data: scheduleEventsUser },
      { data: scheduleEventsEmp },
      { data: workOrders },
      { data: ptoRecs },
    ] = await Promise.all([
      // Schedule events by user_id
      supabase
        .from('schedule_events')
        .select('*')
        .eq('user_id', resolvedUserId)
        .lt('start_time', endISO)
        .gt('end_time', startISO),
      // Schedule events by employee_id (if different)
      resolvedEmployeeId
        ? supabase
          .from('schedule_events')
          .select('*')
          .eq('employee_id', resolvedEmployeeId)
          .lt('start_time', endISO)
          .gt('end_time', startISO)
        : Promise.resolve({ data: null }),
      // Scheduled work orders (unified system)
      supabase
        .from('work_orders')
        .select('id,title,scheduled_start,scheduled_end,assigned_to')
        .eq('assigned_to', resolvedUserId)
        .in('status', ['scheduled', 'in_progress'])
        .lt('scheduled_start', endISO)
        .gt('scheduled_end', startISO),
      // Time off as blocking events. There is no approval step (ruling 20260824a): rows are
      // written PENDING and take effect immediately, so only REJECTED/CANCELLED stop blocking —
      // the same predicate useEmployeesPageData and get_available_time_slots_consolidated use.
      resolvedEmployeeId
        ? supabase
          .from('employee_time_off')
          .select('kind,starts_at,ends_at')
          .eq('employee_id', resolvedEmployeeId)
          .not('status', 'in', '(REJECTED,CANCELLED)')
          .lt('starts_at', endISO)
          .gt('ends_at', startISO)
        : Promise.resolve({ data: null }),
    ]);

    if (scheduleEventsUser) {
      allEvents = allEvents.concat(scheduleEventsUser);
    }

    if (resolvedEmployeeId && scheduleEventsEmp) {
      // merge distinct by id
      const existingIds = new Set(allEvents.map(e => e.id));
      allEvents = allEvents.concat(scheduleEventsEmp.filter(e => !existingIds.has(e.id)));
    }

    if (workOrders) {
      console.log(`🔧 Found ${workOrders.length} work_orders for employee ${employeeId}:`, workOrders);
      // Convert work orders to schedule event format
      const workOrderEvents = workOrders.map(wo => ({
        id: wo.id,
        title: wo.title,
        start_time: wo.scheduled_start,
        end_time: wo.scheduled_end,
        employee_id: wo.assigned_to,
        event_type: 'work_order'
      }));
      allEvents = [...allEvents, ...workOrderEvents];
    }

    if (resolvedEmployeeId && ptoRecs) {
      const ptoEvents = ptoRecs.map(r => ({ title: r.kind || 'PTO', start_time: r.starts_at, end_time: r.ends_at, employee_id: resolvedEmployeeId, event_type: 'pto' }));
      allEvents = [...allEvents, ...ptoEvents];
    }

    console.log(`🎯 TOTAL EVENTS for conflict detection: ${allEvents.length}`, allEvents);
    return allEvents;
  } catch (error) {
    console.error('Error fetching employee schedule:', error);
    return [];
  }
};

/**
 * Check if a time slot conflicts with existing events
 * @param {Date} startTime - Proposed start time
 * @param {Date} endTime - Proposed end time
 * @param {Array} existingEvents - Array of existing schedule events
 * @param {Object} bufferSettings - Buffer settings object
 * @returns {boolean} - True if there's a conflict
 */
// GPT-5 fix: Correct conflict detection with proper numeric comparison
const toMs = (d) => new Date(d).getTime();

export const hasTimeConflict = (existingEvents, slotStart, slotEnd, bufferSettings = {}) => {
  const s = toMs(slotStart);
  const e = toMs(slotEnd);

  // Guard against invalid slots
  if (!(s < e)) return true;

  const bufferBefore = (bufferSettings.default_buffer_before_minutes ?? bufferSettings.job_buffer_minutes ?? 0) * 60 * 1000;
  const bufferAfter = (bufferSettings.default_buffer_after_minutes ?? bufferSettings.job_buffer_minutes ?? 0) * 60 * 1000;

  return existingEvents.some(event => {
    const es = toMs(event.start_time);
    const ee = toMs(event.end_time);

    // Add buffer time to existing events
    const bufferedStart = es - bufferBefore;
    const bufferedEnd = ee + bufferAfter;

    // Strict overlap check: slot overlaps with buffered event
    const hasOverlap = s < bufferedEnd && e > bufferedStart;

    if (hasOverlap) {
      // FIXED: Only log conflicts in debug mode to prevent console spam
      if (window.DEBUG_SCHEDULING) {
        console.log(`CONFLICT DETECTED:`, {
          proposedSlot: { start: new Date(s).toISOString(), end: new Date(e).toISOString() },
          existingEvent: {
            title: event.title,
            start: new Date(es).toISOString(),
            end: new Date(ee).toISOString(),
            bufferedStart: new Date(bufferedStart).toISOString(),
            bufferedEnd: new Date(bufferedEnd).toISOString()
          },
          bufferMinutes: { before: bufferBefore/60000, after: bufferAfter/60000 }
        });
      }
    }

    return hasOverlap;
  });
};

/**
 * Check if time slot is within business hours
 * @param {Date} startTime - Start time to check
 * @param {Date} endTime - End time to check
 * @param {Object} settings - Scheduling settings
 * @returns {boolean} - True if within business hours
 */
export const isWithinBusinessHours = (startTime, endTime, settings) => {
  const dayOfWeek = startTime.getDay();

  // Check if it's a working day
  if (!settings.working_days.includes(dayOfWeek)) {
    return false;
  }

  // Parse business hours
  const [startHour, startMinute] = settings.business_hours_start.split(':').map(Number);
  const [endHour, endMinute] = settings.business_hours_end.split(':').map(Number);

  const businessStart = new Date(startTime);
  businessStart.setHours(startHour, startMinute, 0, 0);

  const businessEnd = new Date(startTime);
  businessEnd.setHours(endHour, endMinute, 0, 0);

  return startTime >= businessStart && endTime <= businessEnd;
};

/**
 * Generate available time slots for an employee
 * @param {string} employeeId - Employee ID
 * @param {number} durationMinutes - Job duration in minutes
 * @param {Date} searchStartDate - Start date for search
 * @param {Date} searchEndDate - End date for search
 * @param {Object} settings - Scheduling settings
 * @returns {Array} - Array of available time slots
 */
export const findAvailableTimeSlots = async (employeeId, durationMinutes, searchStartDate, searchEndDate, settings) => {
  try {
    console.log('🔍 findAvailableTimeSlots START:', { employeeId, durationMinutes, searchStartDate: searchStartDate.toISOString(), searchEndDate: searchEndDate.toISOString(), company_id: settings.company_id });

    // Get existing schedule events
    const existingEvents = await getEmployeeSchedule(employeeId, settings.company_id, searchStartDate, searchEndDate);
    console.log('📅 Existing events for employee:', existingEvents.length, existingEvents);

    // Phase 1: capacity guard per employee (hours/day)
    let capacityHoursPerDay = 8;
    try {
      const { supabase } = await import('./supabaseClient');
      const { data: rows } = await supabase
        .from('employees')
        .select('id,capacity_hours_per_day')
        .eq('user_id', employeeId);

      if (rows && rows.length > 0) {
        const v = parseFloat(rows[0].capacity_hours_per_day);
        if (!isNaN(v) && v > 0) capacityHoursPerDay = v;
      }
    } catch (e) {
      console.warn('Capacity fetch failed, defaulting to 8h/day');
    }
    console.log('💼 Capacity hours per day:', capacityHoursPerDay);

    const availableSlots = [];
    const slotDurationMs = durationMinutes * 60 * 1000;
    console.log('⏱️ Slot duration (ms):', slotDurationMs, '(', durationMinutes, 'minutes )');

    // helper: compute already booked minutes for the slot's day
    const calcBookedMinutesForDay = (dayStart) => {
      const start = new Date(dayStart);
      start.setHours(0,0,0,0);
      const end = new Date(dayStart);
      end.setHours(23,59,59,999);
      let totalMs = 0;
      for (const ev of existingEvents) {
        const es = new Date(ev.start_time).getTime();
        const ee = new Date(ev.end_time).getTime();
        const overlapStart = Math.max(start.getTime(), es);
        const overlapEnd = Math.min(end.getTime(), ee);
        if (overlapEnd > overlapStart) {
          totalMs += (overlapEnd - overlapStart);
        }
      }
      return Math.round(totalMs / 60000);
    };

    // Ensure we don't schedule too far in advance or too soon
    const minBookingTime = new Date();
    minBookingTime.setHours(minBookingTime.getHours() + settings.min_advance_booking_hours);

    const maxBookingTime = new Date();
    maxBookingTime.setDate(maxBookingTime.getDate() + settings.max_advance_booking_days);

    let actualStartDate = new Date(Math.max(searchStartDate.getTime(), minBookingTime.getTime()));
    let actualEndDate = new Date(Math.min(searchEndDate.getTime(), maxBookingTime.getTime()));

    // Debug: show gating inputs/outputs
    console.log('⏳ GATING', {
      now: new Date().toISOString(),
      min_advance_booking_hours: settings.min_advance_booking_hours,
      max_advance_booking_days: settings.max_advance_booking_days,
      searchStartDate: searchStartDate.toISOString(),
      searchEndDate: searchEndDate.toISOString(),
      minBookingTime: minBookingTime.toISOString(),
      maxBookingTime: maxBookingTime.toISOString(),
      actualStartDate: actualStartDate.toISOString(),
      actualEndDate: actualEndDate.toISOString()
    });

    // Guard: if window collapsed due to gating, expand minimally to avoid zero results
    if (actualEndDate <= actualStartDate) {
      console.warn('⚠️ Search window collapsed after gating. Adjusting minimally for diagnostics.');
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      const adjustedEnd = new Date(Math.min(
        searchEndDate.getTime(),
        actualStartDate.getTime() + sevenDays,
        maxBookingTime.getTime()
      ));
      console.log('⏳ ADJUSTED WINDOW', {
        before: { start: actualStartDate.toISOString(), end: actualEndDate.toISOString() },
        after: { start: actualStartDate.toISOString(), end: adjustedEnd.toISOString() }
      });
      actualEndDate = adjustedEnd;
    }

    // Generate clean 15-minute interval time slots
    const cleanTimeSlots = generateCleanTimeSlots(actualStartDate, actualEndDate, 15);
    console.log('🕐 Generated clean time slots:', cleanTimeSlots.length, 'slots from', actualStartDate.toISOString(), 'to', actualEndDate.toISOString());

    // Check each clean time slot for availability
    let skippedBeyondEnd = 0, skippedBusinessHours = 0, skippedCapacity = 0, skippedConflict = 0;
    for (const slotStartTime of cleanTimeSlots) {
      const slotEndTime = new Date(slotStartTime.getTime() + slotDurationMs);

      // Skip if slot extends beyond search end date
      if (slotEndTime > actualEndDate) {
        skippedBeyondEnd++;
        continue;
      }

      // Check if within business hours
      if (!isWithinBusinessHours(slotStartTime, slotEndTime, settings)) {
        skippedBusinessHours++;
        continue;
      }

      // Capacity guard: total booked minutes for the day + this job cannot exceed capacity
      const bookedMinutes = calcBookedMinutesForDay(slotStartTime);
      if ((bookedMinutes + durationMinutes) > (capacityHoursPerDay * 60)) {
        skippedCapacity++;
        console.log('⛔ Slot exceeds capacity:', slotStartTime.toISOString(), '- booked:', bookedMinutes, 'min, needed:', durationMinutes, 'min, capacity:', capacityHoursPerDay * 60, 'min');
        continue;
      }

      // Check for conflicts using enhanced buffer settings (GPT-5 fix: correct parameter order)
      const hasConflict = hasTimeConflict(existingEvents, slotStartTime, slotEndTime, settings);
      if (!hasConflict) {
        console.log('✅ SLOT ADDED:', slotStartTime.toISOString(), 'to', slotEndTime.toISOString());
        availableSlots.push({
          start_time: new Date(slotStartTime),
          end_time: new Date(slotEndTime),
          duration_minutes: durationMinutes,
          employee_id: employeeId,
          buffer_before: settings.default_buffer_before_minutes ?? 30,
          buffer_after: settings.default_buffer_after_minutes ?? 30,
          is_clean_interval: true // Flag to indicate this is a professionally rounded time
        });
      } else {
        skippedConflict++;
      }
    }
    console.log('📊 Slot filtering summary:', { total: cleanTimeSlots.length, skippedBeyondEnd, skippedBusinessHours, skippedCapacity, skippedConflict, available: availableSlots.length });



    console.log(`🎯 CONFLICT SUMMARY for employee ${employeeId}:`);
    console.log(`   - Existing events: ${existingEvents.length}`);
    console.log(`   - Available slots: ${availableSlots.length}`);
    console.log(`   - Duration: ${durationMinutes} minutes`);
    console.log(`   - Search range: ${searchStartDate.toISOString()} to ${searchEndDate.toISOString()}`);

    return availableSlots;
  } catch (error) {
    console.error('Error finding available time slots:', error);
    return [];
  }
};

/**
 * Get smart scheduling suggestions for multiple employees
 * @param {Array} employeeIds - Array of employee IDs
 * @param {number} durationMinutes - Job duration in minutes
 * @param {string} companyId - Company ID
 * @param {Date} preferredDate - Preferred date (optional)
 * @returns {Object} - Suggestions grouped by employee
 */
export const getSmartSchedulingSuggestions = async (employeeIds, durationMinutes, companyId, preferredStartDate = null, preferredEndDate = null) => {
  try {
    const settings = await getSchedulingSettings(companyId);

    // Set search date range - respect user's date range selection
    const searchStartDate = preferredStartDate || new Date();
    const searchEndDate = preferredEndDate || new Date(searchStartDate.getTime() + 14 * 24 * 60 * 60 * 1000); // Default 2 weeks if not specified

    /* console.log('🧭 getSmartSchedulingSuggestions range', {
      preferredStartDate,
      preferredEndDate,
      searchStartDate: searchStartDate.toISOString(),
      searchEndDate: searchEndDate.toISOString(),
      min_advance_booking_hours: settings.min_advance_booking_hours,
      max_advance_booking_days: settings.max_advance_booking_days,
      employeeCount: employeeIds?.length || 0,
      durationMinutes
    }); */

    // Try backend RPC first (single source of truth); fall back to client logic if unavailable
    try {
      const { supabase } = await import('./supabaseClient');
      
      // Use get_available_time_slots which returns TABLE format (array of rows)
      // This is much faster than client-side computation
      const { data: rows, error: rpcError } = await supabase
        .rpc('get_available_time_slots', {
          p_company_id: companyId,
          p_employee_ids: employeeIds,
          p_duration_minutes: durationMinutes,
          p_start: searchStartDate.toISOString(),
          p_end: searchEndDate.toISOString()
        });

      /**
       * Board #429. This used to require `rows.length > 0` to accept the answer, so a
       * SUCCESSFUL empty array was treated as a failure: it logged "RPC failed or returned
       * empty", then recomputed the whole thing client-side, which correctly returned nothing
       * too. One query became N, and the console said the backend was broken when it was not.
       *
       * Zero slots is a legitimate answer. Jerry's case was an 8-hour job requested late in a
       * "This week" range that ends at the start of Saturday, against a 24-hour advance-notice
       * setting — the effective window began Friday after 10:00, and reconstruction of the live
       * function gave 23 slots at 120 minutes, 15 at 240, 7 at 360 and 0 at 480. The RPC was
       * right; nothing explained it.
       *
       * An ERROR still falls through to the client fallback, which is what a fallback is for.
       */
      if (!rpcError && Array.isArray(rows)) {
        console.log('[SCHED-DEBUG] RPC returned', rows?.length, 'rows');
        console.log('[SCHED-DEBUG] First 3 RPC rows:', JSON.stringify(rows.slice(0, 3).map(r => ({ employee_id: r.employee_id, user_id: r.user_id, start_time: r.start_time }))));
        const suggestions = {};
        for (const row of rows) {
          const empUserId = row.user_id || row.employee_id || null; // prefer user_id for grouping
          if (!empUserId) continue;
          if (!suggestions[empUserId]) {
            suggestions[empUserId] = { employee_id: empUserId, available_slots: [], total_available: 0 };
          }
          suggestions[empUserId].available_slots.push({
            start_time: new Date(row.start_time),
            end_time: new Date(row.end_time),
            duration_minutes: row.duration_minutes || durationMinutes,
            employee_id: empUserId,
            buffer_before: settings.default_buffer_before_minutes ?? 30,
            buffer_after: settings.default_buffer_after_minutes ?? 30,
            is_clean_interval: true
          });
        }
        Object.values(suggestions).forEach(s => s.total_available = s.available_slots.length);
        console.log('[SCHED-DEBUG] Suggestion keys (empUserIds):', Object.keys(suggestions));
        console.log('[SCHED-DEBUG] Slots per key:', JSON.stringify(Object.entries(suggestions).map(([k, v]) => ({ key: k, slots: v.available_slots.length }))));

        return {
          suggestions,
          settings,
          search_period: { start: searchStartDate, end: searchEndDate },
          // What the answer came from, and — when it is zero — the constraints that produced
          // it, so the UI can say WHY instead of showing an unexplained blank. A user told
          // "no availability" with ten idle staff assumes the app is broken; told "an 8-hour
          // job needs a full shift, and 24 hours' notice leaves only Friday in this range",
          // they shorten the job or widen the range.
          source: 'rpc',
          no_availability: rows.length === 0,
          constraints: rows.length === 0 ? {
            duration_minutes: durationMinutes,
            // The real column name, verified in the settings select at the top of this file —
            // guessing it would have produced `undefined` and a message that omitted the very
            // constraint that caused the empty result.
            min_advance_booking_hours: settings.min_advance_booking_hours ?? null,
            search_start: searchStartDate,
            search_end: searchEndDate,
            employees_considered: employeeIds.length
          } : null
        };
      } else if (rpcError) {
        // A real failure — the fallback exists for exactly this.
        console.warn('RPC get_available_time_slots errored; falling back to client logic:', rpcError.message);
      } else {
        console.warn('RPC get_available_time_slots returned an unexpected shape; falling back to client logic.');
      }
    } catch (rpcErr) {
      console.warn('RPC get_available_time_slots error; falling back to client logic:', rpcErr?.message || rpcErr);
    }

    // Fallback: existing client-side per-employee computation
    const suggestions = {};
    for (const employeeId of employeeIds) {
      const slots = await findAvailableTimeSlots(
        employeeId,
        durationMinutes,
        searchStartDate,
        searchEndDate,
        settings
      );

      suggestions[employeeId] = {
        employee_id: employeeId,
        available_slots: slots, // Show all available slots
        total_available: slots.length
      };
    }

    return {
      suggestions,
      settings,
      search_period: { start: searchStartDate, end: searchEndDate }
    };
  } catch (error) {
    console.error('Error getting smart scheduling suggestions:', error);
    return { suggestions: {}, settings: DEFAULT_SCHEDULING_SETTINGS };
  }
};

/**
 * Create a schedule event
 * @param {Object} eventData - Event data
 * @returns {Object} - Created event or error
 */
export const createScheduleEvent = async (eventData) => {
  try {
    const { supabase } = await import('./supabaseClient');
    const { data: events, error } = await supabase
      .from('schedule_events')
      .insert({
        ...eventData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select();

    if (!error && events) {
      return { success: true, event: events[0] };
    } else {
      return { success: false, error: error?.message };
    }
  } catch (error) {
    console.error('Error creating schedule event:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Format time slot for display with clean professional formatting
 * @param {Object} slot - Time slot object
 * @returns {string} - Formatted time slot
 */
export const formatTimeSlot = (slot) => {
  const startTime = new Date(slot.start_time);
  const endTime = new Date(slot.end_time);

  const dateStr = fmtDate(startTime, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });

  // Use clean formatting for professional appearance
  const timeStr = `${fmtTime(startTime, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })} - ${fmtTime(endTime, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })}`;

  // Add indicator for clean intervals
  const cleanIndicator = slot.is_clean_interval ? ' ✨' : '';

  return `${dateStr} at ${timeStr}${cleanIndicator}`;
};

/**
 * Format time for display in 12-hour format with clean intervals
 * @param {Date} date - Date to format
 * @returns {string} - Formatted time
 */
export const formatCleanTime = (date) => {
  return fmtTime(date, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Get duration display string
 * @param {number} minutes - Duration in minutes
 * @returns {string} - Formatted duration
 */
export const formatDuration = (minutes) => {
  if (minutes < 60) {
    return `${minutes} min`;
  } else {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hr${hours > 1 ? 's' : ''}`;
    } else {
      return `${hours}h ${remainingMinutes}m`;
    }
  }
};

/**
 * Skip scheduling and start job immediately
 * For jobs that don't need scheduling (nights/weekends/as-needed)
 * @param {string} workOrderId - Work order ID
 * @param {string} reason - Reason for skipping scheduling
 * @returns {Object} - Result with success status
 */
export const skipSchedulingAndStartJob = async (workOrderId, reason = null) => {
  try {
    console.log('⏭️ Skipping scheduling for work order:', workOrderId);

    // SECURITY: Use authenticated Supabase client
    const supabase = getSupabaseClient();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('User not authenticated');
    }

    const { data: result, error } = await supabase.rpc('skip_scheduling_and_start_job', {
      p_work_order_id: workOrderId,
      p_notes: reason
    });

    if (error) {
      console.error('❌ Failed to skip scheduling:', error);
      return { success: false, error: error.message };
    }

    if (result && result.success) {
      console.log('✅ Job started immediately:', result);
      return { success: true, data: result };
    } else {
      console.error('❌ Failed to skip scheduling:', result?.error);
      return { success: false, error: result?.error || 'Unknown error' };
    }
  } catch (error) {
    console.error('❌ Error skipping scheduling:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Change work order status manually
 * For flexible status transitions with validation
 * @param {string} workOrderId - Work order ID
 * @param {string} newStatus - New status
 * @param {string} reason - Reason for change
 * @returns {Object} - Result with success status
 */
export const changeWorkOrderStatus = async (workOrderId, newStatus, reason = null) => {
  try {
    console.log('🔄 Changing work order status:', { workOrderId, newStatus, reason });

    // SECURITY: Use authenticated Supabase client
    const supabase = getSupabaseClient();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('User not authenticated');
    }

    const { data: result, error } = await supabase.rpc('transition_work_order_status', {
      p_work_order_id: workOrderId,
      p_new_status: newStatus
    });

    if (error) {
      console.error('❌ Failed to change status:', error);
      return { success: false, error: error.message };
    }

    // RPC is void — no error means success
    console.log('✅ Status changed successfully to:', newStatus);
    return { success: true };
  } catch (error) {
    console.error('❌ Error changing status:', error);
    return { success: false, error: error.message };
  }
};

// ============================================================
// Multi-Day Job Scheduling
// ============================================================

// (DAY_NAMES is declared once at the top of this module.)

/**
 * Parse a time string like '08:00:00' or '08:00' into {hours, minutes}
 */
const parseTimeString = (timeStr) => {
  const parts = (timeStr || '08:00').split(':');
  return { hours: parseInt(parts[0], 10), minutes: parseInt(parts[1] || '0', 10) };
};

/**
 * Check if a given day-of-week (0=Sun..6=Sat) is a working day
 */
const isWorkingDay = (dayOfWeek, workingDays) => {
  if (!workingDays) return dayOfWeek >= 1 && dayOfWeek <= 5; // default Mon-Fri
  const name = DAY_NAMES[dayOfWeek];
  return !!workingDays[name];
};

/**
 * Expand total job hours into daily business-hour blocks.
 *
 * @param {number} totalHours - Total hours to schedule (e.g. 24, 144)
 * @param {Date}   startDate  - First possible calendar day
 * @param {string} businessHoursStart - e.g. '08:00:00'
 * @param {string} businessHoursEnd   - e.g. '17:00:00'
 * @param {Object} workingDays - e.g. {monday:true, ..., saturday:false, sunday:false}
 * @returns {Array<{date:Date, startTime:Date, endTime:Date, hours:number}>}
 */
export const expandJobToBusinessDays = (
  totalHours,
  startDate,
  businessHoursStart,
  businessHoursEnd,
  workingDays
) => {
  const start = parseTimeString(businessHoursStart);
  const end   = parseTimeString(businessHoursEnd);
  const hoursPerDay = (end.hours + end.minutes / 60) - (start.hours + start.minutes / 60);

  if (hoursPerDay <= 0) {
    console.error('⚠️ expandJobToBusinessDays: businessHoursEnd must be after businessHoursStart');
    return [];
  }

  const blocks = [];
  let remaining = totalHours;
  const cursor = new Date(startDate);
  cursor.setHours(0, 0, 0, 0);

  // Safety: cap at 365 iterations to prevent infinite loops
  let safety = 0;
  while (remaining > 0 && safety < 365) {
    safety++;
    const dow = cursor.getDay();

    if (isWorkingDay(dow, workingDays)) {
      const allocate = Math.min(remaining, hoursPerDay);
      const allocateMs = allocate * 3600000;

      const dayStart = new Date(cursor);
      dayStart.setHours(start.hours, start.minutes, 0, 0);

      const dayEnd = new Date(dayStart.getTime() + allocateMs);

      blocks.push({
        date: new Date(cursor),
        startTime: dayStart,
        endTime: dayEnd,
        hours: Math.round(allocate * 100) / 100,
      });

      remaining -= allocate;
    }

    // Advance to next day
    cursor.setDate(cursor.getDate() + 1);
  }

  return blocks;
};

/**
 * Create schedule_events for a multi-day (or single-day) job.
 *
 * Fetches company business hours, expands the job into daily blocks,
 * inserts schedule_events rows, and optionally updates the work order's
 * scheduled_start / scheduled_end to span the full range.
 *
 * @param {string} workOrderId
 * @param {string} companyId
 * @param {string} assignedTo    - user ID of assigned technician
 * @param {number} totalHours    - total estimated hours for the job
 * @param {Date}   startDate     - desired first day
 * @param {string} [title]       - event title (falls back to 'Scheduled Work')
 * @returns {Promise<{success:boolean, events?:Array, error?:string}>}
 */
export const createScheduleEventsForJob = async (
  workOrderId,
  companyId,
  assignedTo,
  totalHours,
  startDate,
  title
) => {
  try {
    const supabase = getSupabaseClient();

    // 1. Get company scheduling settings
    const settings = await getSchedulingSettings(companyId);
    const businessStart = settings.business_hours_start || '08:00:00';
    const businessEnd   = settings.business_hours_end   || '17:00:00';
    const workingDays   = settings.working_days || null; // null → defaults to Mon-Fri

    // 2. Expand into daily blocks
    const blocks = expandJobToBusinessDays(
      totalHours,
      startDate,
      businessStart,
      businessEnd,
      workingDays
    );

    if (!blocks.length) {
      return { success: false, error: 'Could not generate schedule blocks' };
    }

    // 3. Remove any existing schedule_events for this work order
    await supabase
      .from('schedule_events')
      .delete()
      .eq('work_order_id', workOrderId);

    // 4. Insert new schedule_events
    const rows = blocks.map((block, idx) => ({
      company_id: companyId,
      work_order_id: workOrderId,
      user_id: assignedTo,
      title: title || 'Scheduled Work',
      description: `Day ${idx + 1} of ${blocks.length} (${block.hours}h)`,
      start_time: block.startTime.toISOString(),
      end_time: block.endTime.toISOString(),
      status: 'scheduled',
    }));

    const { data: created, error: insertErr } = await supabase
      .from('schedule_events')
      .insert(rows)
      .select();

    if (insertErr) {
      console.error('❌ Failed to create schedule events:', insertErr);
      return { success: false, error: insertErr.message };
    }

    // 5. Update work order scheduled_start/end to span the full range
    const firstBlock = blocks[0];
    const lastBlock  = blocks[blocks.length - 1];
    await supabase
      .from('work_orders')
      .update({
        scheduled_start: firstBlock.startTime.toISOString(),
        scheduled_end:   lastBlock.endTime.toISOString(),
      })
      .eq('id', workOrderId);

    console.log(`✅ Created ${created.length} schedule events for WO ${workOrderId}`);
    return { success: true, events: created };
  } catch (error) {
    console.error('❌ createScheduleEventsForJob error:', error);
    return { success: false, error: error.message };
  }
};
